import {
  ZENDESK_CUSTOM_FIELD_IDS_PRODUCTION,
  ZENDESK_CUSTOM_FIELD_IDS_SANDBOX,
  ZENDESK_PRODUCTION_URL,
  ZENDESK_SANDBOX_URL,
} from "@zapier/support-shared-constants";
import { isProduction } from "@/utils/environment";

// Service constants
export const MAX_SERVICE_LINKS_TO_DISPLAY = 5;

// Event constants
export const EVENT_CONTACT_PREFIX = "contact_form_";
export const EVENT_CONTACT_GENERIC = `${EVENT_CONTACT_PREFIX}clicked_link`;
export const EVENT_CONTACT_LOG_IN = `${EVENT_CONTACT_PREFIX}user_login`;
export const EVENT_CONTACT_SIGN_UP = `${EVENT_CONTACT_PREFIX}user_signup`;
export const EVENT_CONTACT_CONTINUE_LOGGED_OUT = `${EVENT_CONTACT_PREFIX}continue_logged_out`;
export const EVENT_CONTACT_INIT = `${EVENT_CONTACT_PREFIX}opened_form`;
export const EVENT_CONTACT_STEP = `${EVENT_CONTACT_PREFIX}changed_node`;
export const EVENT_CONTACT_SUBMITTED = `${EVENT_CONTACT_PREFIX}submit`;
export const EVENT_CONTACT_ERROR = `${EVENT_CONTACT_PREFIX}error_sending_form`;
export const EVENT_CONTACT_SELF_SERVE_RESOURCE_CLICK = `${EVENT_CONTACT_PREFIX}self_serve_resource_click`;
export const EVENT_CONTACT_THIS_DIDNT_ANSWER = `${EVENT_CONTACT_PREFIX}this_didnt_answer_my_question`;
export const EVENT_CONTACT_FEDERATED_SEARCH = `federated_search_entered`;
export const EVENT_CONTACT_FEDERATED_SEARCH_SELECTION = `federated_search_selection`;
export const EVENT_CONTACT_COPILOT_ENGAGED = `${EVENT_CONTACT_PREFIX}copilot_engaged`;
export const EVENT_CONTACT_COPILOT_BACK_TO_FORM = `${EVENT_CONTACT_PREFIX}copilot_back_to_form`;

// CSS constants
export const CSS_CONTAINER_MAX_WIDTH = `${20 + 746 + 20}px`;
export const CSS_CONTAINER_DEFAULT_PADDING = "0 20px";

// Analytics events
export const EVENT_SYSTEM_NAME = "supportfrontend";
export const PAGE_VIEW_EVENT = "web_analytics.tracking.page_view.PageViewEvent";
export const USER_INTERACTION_EVENT =
  "web_analytics.tracking.user_interaction.UserInteractionEvent";

export const CONTACT_FORM_INTERACTION_EVENT =
  "support.supportfrontend.ContactFormInteractionEvent";

export const COPILOT_SOURCE = {
  widget: "chat-widget",
  fullscreen: "get-help",
} as const;

// Support constants
export const DAYS_FOR_PAID_SUPPORT_AFTER_TRIAL = 30;
export const USER_TIMEZONE_ZD_TAG_PREFIX = "browser-tz-";

// Contact form subject/message length limits.
// Field caps are aligned to Zendesk's actual limits: the ticket subject caps at
// 150 chars, and the end-user (Web Widget) comment body caps at 4096 chars
// (well under Zendesk's 65,535 hard limit). We don't use the web widget, but it
// seemed like a reasonable sanity limit. Query-param prefill caps are smaller
// to leave the user editing runway before hitting the field limit.
export const SUBJECT_MAX_LENGTH = 150;
export const MESSAGE_MAX_LENGTH = 4096;
export const SUBJECT_PREFILL_MAX_LENGTH = 150;
export const MESSAGE_PREFILL_MAX_LENGTH = 3000;

// Zendesk instance URLs, from @zapier/support-shared-constants
export { ZENDESK_PRODUCTION_URL, ZENDESK_SANDBOX_URL };

/**
 * Get the appropriate Zendesk Support Portal URL based on environment
 * Used for schedule checks and other Zendesk Support API calls
 */
export const getZendeskSupportUrl = (isProd: boolean = isProduction) =>
  isProd ? ZENDESK_PRODUCTION_URL : ZENDESK_SANDBOX_URL;

// Zendesk custom field IDs (environment-specific), from
// @zapier/support-shared-constants. Sandbox IDs are refreshed there after a
// sandbox rebuild. View the fields in Zendesk Admin:
// https://zapier.zendesk.com/admin/objects-rules/tickets/ticket-fields
// Critical-ticket field IDs are sourced dynamically from supportbff via
// /api/supportfrontend/v1/proxy/zendesk/field-ids — see useCriticalTicketFieldConfig.
// Notes on specific fields:
// - OTHER_TOOLS_WHICH_TOOL is titled just `Product` in Zendesk (not `CF:{name}`)
//   because it can be set in other ways besides the contact form.
// - WHICH_EARLY_ACCESS_PRODUCT holds the selected Early Access product (e.g.
//   "cf-next-gen-zap-workflows"); the parent "Early Access Products" category
//   value is recorded in MORE_INFORMATION_REQUEST.
export const getZendeskCustomFieldIds = (isProd: boolean = isProduction) =>
  isProd
    ? ZENDESK_CUSTOM_FIELD_IDS_PRODUCTION
    : ZENDESK_CUSTOM_FIELD_IDS_SANDBOX;

// Backwards compatibility constant
export const ZENDESK_CUSTOM_FIELD_IDS = getZendeskCustomFieldIds();

// Zendesk CSAT constants
export const ZENDESK_CSAT_SURVEY_LOCALE = "en-us";
export const ZENDESK_EMAIL_CHANNEL = "web";
// If no CSAT survey exists, create one at https://<zendesk-url>/admin/objects-rules/rules/csat
// IDs for current active survey schema can be found via GET /api/v2/guide/en-us/surveys
export const ZENDESK_CSAT_SURVEY_ID = isProduction
  ? "01JNJY8TTRVPJK5ZNJ4QP7WKQC"
  : "01KKA2F3GBT2EQ9A3TKGKXK7BJ";
export const ZENDESK_CSAT_RATING_SCALE_ID = isProduction
  ? "01JNJY8R39CB7QFBGE3R96H033"
  : "01KKA2F1W3ZM1GDD0PJNYWNMX4";
export const ZENDESK_CSAT_OPEN_ENDED_COMMENT_ID = isProduction
  ? "01JNJY8R3ATG6MDDPBXW4Z8NB6"
  : "01KKA2F1W4GYP4MHM4RWKWNFZF";
// Zendesk requires us to setup an OAuth client: https://<zendesk-url>/admin/apps-integrations/apis/oauth-clients
// Used to connect to the Zendesk CSAT survey response endpoint to submit answers on an end user's behalf
// Client ID is not exposed in the UI. To fetch it, GET /api/v2/oauth/clients.json
export const ZENDESK_CSAT_SURVEY_OAUTH_CLIENT_ID = isProduction
  ? 41246911536269
  : 44144675877645;

// Images
export const DEFAULT_SERVICE_ICON_URL_GREY =
  "https://cdn.zapier.com/zapier/images/frontend/placeholder/128x128.grey.png";
export const DEFAULT_SERVICE_ICON_URL_ORANGE =
  "https://cdn.zapier.com/zapier/images/frontend/placeholder/128x128.png";
export const UPGRADE_TO_GET_BETTER_SUPPORT_CTA_HEADER =
  "https://zapier-images.imgix.net/storage/photos/1728c40b6993f25d35bd1cd39d0e9355.png";

// URLs and paths
export const ZAPIER_BASE_URL = process.env.NEXT_PUBLIC_ZAPIER_ORIGIN || "";
export const SUPPORTBFF_PATH = "/api/supportbff/v1/zapier_contact_form";
export const SUPPORTBFF_URL = `${ZAPIER_BASE_URL}${SUPPORTBFF_PATH}`;
export const GET_HELP_PATH = "/app/get-help";
export const GET_HELP_CTA = "get help";
export const PROD_HELP_CENTER_URL = "https://help.zapier.com/hc/en-us";
export const HELP_CENTER_SOURCE = "360000902071";
export const HELP_CENTER_TYPE = "knowledge_base";
export const PROD_COMMUNITY_URL = "https://community.zapier.com";

// React Query configuration for get-help queries
// These settings ensure:
// - Data stays fresh for 10 minutes before becoming stale
// - Cache persists for 12 minutes (longer than staleTime to avoid premature garbage collection)
// - Automatic refetch on window focus to catch login/account changes
export const QUERY_CONFIG = {
  staleTime: 10 * 60 * 1000, // 10 minutes
  cacheTime: 12 * 60 * 1000, // 12 minutes - longer than staleTime to avoid premature garbage collection
  refetchOnWindowFocus: true, // Refetch on window focus to catch login/account changes
  refetchOnReconnect: false, // Don't refetch on reconnect
  refetchOnMount: false, // Don't refetch on mount if data exists
} as const;
